import { Download, FileText, Search } from "lucide-react";
import type { ReactNode } from "react";
import { Divider } from "../atoms/Divider";
import { Heading } from "../atoms/Heading";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import {
	HelpTableOfContents,
	type TocItem,
} from "../molecules/HelpTableOfContents";
import { BlockchainExplainerDiagram } from "../organisms/BlockchainExplainerDiagram";
import { TaskLauncher, type TaskLauncherItem } from "../organisms/TaskLauncher";

export interface HelpPageProps {
	className?: string;
}

const TASKS: TaskLauncherItem[] = [
	{
		id: "task-verify-event",
		title: "Verify an event",
		description: "Confirm a single action is locked into the public record.",
		href: "/events",
		icon: <Search className="h-5 w-5" aria-hidden="true" />,
	},
	{
		id: "task-audit-loan",
		title: "Audit a loan",
		description: "Review every action on a loan file against the record.",
		href: "/loans",
		icon: <FileText className="h-5 w-5" aria-hidden="true" />,
	},
	{
		id: "task-get-certificate",
		title: "Get a certificate",
		description: "Download a signed proof you can share with others.",
		href: "/reports",
		icon: <Download className="h-5 w-5" aria-hidden="true" />,
	},
];

const TOC_ITEMS: TocItem[] = [
	{ id: "verify-an-event", label: "Verify an event", level: 1 },
	{ id: "audit-a-loan", label: "Audit a loan", level: 1 },
	{ id: "get-certificate", label: "Get a certificate", level: 1 },
	{ id: "why-trust", label: "Why can I trust this?", level: 1 },
	{ id: "privacy", label: "Is my private data exposed?", level: 1 },
	{ id: "learn-more", label: "Where can I learn more?", level: 1 },
];

function HelpPage({ className }: HelpPageProps): ReactNode {
	return (
		<div className={cn("mx-auto max-w-6xl px-4 py-8", className)}>
			<Heading size="2xl" as="h1" className="mb-2">
				How it works
			</Heading>
			<Text textColor="mutedForeground" className="mb-6 max-w-2xl">
				Start with the task you need to do. The “How it works” answers below
				explain why you can trust what the portal shows you.
			</Text>

			<TaskLauncher items={TASKS} headingAs="h2" className="mb-10" />

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_1fr]">
				<aside className="lg:sticky lg:top-4 lg:self-start">
					<HelpTableOfContents items={TOC_ITEMS} />
				</aside>

				<article className="prose max-w-3xl lg:prose-lg">
					<div className="flex flex-col gap-8">
						{/* ---- Using the portal: task-first answers ---- */}

						<section>
							<Heading size="md" as="h2" id="verify-an-event">
								How do I verify an event?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								Open <em>Events</em> and paste the event&rsquo;s fingerprint
								(its hash). The portal shows you the proof and points to where
								the event is recorded on the public ledger. If the proof checks
								out, that action really happened when it says it did and has not
								been changed since.
							</Text>
						</section>

						<section>
							<Heading size="md" as="h2" id="audit-a-loan">
								How do I audit a loan?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								Open <em>Loans</em> and enter the loan ID. You get the full
								timeline of actions on that file, and each one links to its own
								proof. The loan also shows its RESPA coverage — how many of the
								13 settlement-service categories have recorded actions — so you
								can see at a glance what is documented.
							</Text>
						</section>

						<section>
							<Heading size="md" as="h2" id="get-certificate">
								How do I get a certificate?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								Open <em>Reports</em> to generate a signed certificate for a
								loan or a batch. It bundles the proofs into one document you can
								hand to a regulator or keep for your own records.
							</Text>
						</section>

						<Divider />

						{/* ---- How it works: trust methodology ---- */}

						<section>
							<Heading size="md" as="h2" id="why-trust">
								Why can I trust this?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								Every action gets a unique fingerprint. Fingerprints are bundled
								into batches, each batch gets one master fingerprint, and that
								master fingerprint is written to a public ledger that no one can
								edit.
							</Text>
							<Text textColor="mutedForeground" className="mt-2">
								Change any past action — even a single character — and its
								batch&rsquo;s master fingerprint no longer matches what is on
								the ledger. Tampering shows up immediately, and you can check it
								yourself without having to trust us. The diagram below follows
								one action from the moment it is recorded to its permanent
								anchor on the public ledger.
							</Text>
							<BlockchainExplainerDiagram className="mt-4" />
						</section>

						<section>
							<Heading size="md" as="h2" id="privacy">
								Is my private data exposed?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								No. Loan IDs and user IDs are hashed before they leave our
								system, so you only ever see opaque tokens — never names, Social
								Security numbers, or property addresses. We can prove which loan
								an action belongs to, using a key only we hold, but nothing on
								this page can be traced back to a real person.
							</Text>
						</section>

						<section>
							<Heading size="md" as="h2" id="learn-more">
								Where can I learn more?
							</Heading>
							<Text textColor="mutedForeground" className="mt-2">
								Independent background on the technology behind the public
								ledger:
							</Text>
							<ul className="mt-3 flex flex-col gap-1.5 pl-5 text-sm text-mutedForeground list-disc">
								<li>
									<a
										href="https://polygon.technology/"
										target="_blank"
										rel="noreferrer"
										className="text-primary underline underline-offset-2"
									>
										Polygon
									</a>{" "}
									— the public ledger we anchor to
								</li>
								<li>
									<a
										href="https://datatracker.ietf.org/doc/html/rfc6962"
										target="_blank"
										rel="noreferrer"
										className="text-primary underline underline-offset-2"
									>
										RFC 6962 — Certificate Transparency Merkle Proofs
									</a>
								</li>
								<li>
									<a
										href="https://en.wikipedia.org/wiki/Tamper-evident#Logging"
										target="_blank"
										rel="noreferrer"
										className="text-primary underline underline-offset-2"
									>
										Tamper-evident logging
									</a>{" "}
									— Wikipedia
								</li>
							</ul>
						</section>
					</div>
				</article>
			</div>
		</div>
	);
}
HelpPage.displayName = "HelpPage";

export default HelpPage;
export { HelpPage };
